import React, { useEffect, useMemo, useRef, useState } from "react";
import InputField from "components/atoms/InputField";
import InputSelect from "components/atoms/InputSelect";
import InputDateTime from "components/InputDateTime";
import moment from "moment";
import { YesAndNoOptions } from "utils/constant";

const INSECTICIDE_TYPE_OPTIONS = [
  { label: "Organophosphate", value: "Organophosphate" },
  { label: "Karbamat", value: "Karbamat" },
  { label: "Piretroid", value: "Piretroid" },
  { label: "Neonikotinoid", value: "Neonikotinoid" },
];

const INSECTICIDE_UNIT_OPTIONS = [
  { label: "Kg", value: "Kg" },
  { label: "Liter", value: "Liter" },
];

const toInt = (v) =>
  v === "" || v === null || v === undefined ? 0 : parseInt(v, 10) || 0;

const IRSFields = ({
  formData,
  handleInputChange,
  setFormData,
  memoizedValues,
}) => {
  const [rowErrors, setRowErrors] = useState({});
  const [summaryError, setSummaryError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    total_sprayed_house: "",
  });
  const clearTimers = useRef({});

  const setFieldErrorWithAutoClear = (key, msg) => {
    if (clearTimers.current[key]) clearTimeout(clearTimers.current[key]);
    setFieldErrors((prev) => ({ ...prev, [key]: msg }));
    clearTimers.current[key] = setTimeout(() => {
      setFieldErrors((prev) => ({ ...prev, [key]: "" }));
    }, 3000);
  };

  const irs = formData.irs || {};
  const isDusun = irs.is_dusun_level_intervention === "Ya";

  const totalTargeted = toInt(irs.total_targeted_house);
  const totalSprayed = toInt(irs.total_sprayed_house);

  const totalDusunSprayed = useMemo(() => {
    return (irs.sub_villages_data || []).reduce(
      (s, v) => s + toInt(v && v.sprayed_houses),
      0
    );
  }, [irs.sub_villages_data]);

  const totalDusunTargeted = useMemo(() => {
    return (irs.sub_villages_data || []).reduce(
      (s, v) => s + toInt(v && v.targeted_houses),
      0
    );
  }, [irs.sub_villages_data]);

  useEffect(() => {
    if (!isDusun) return;
    if (totalSprayed !== totalDusunSprayed) {
      setFormData((prev) => ({
        ...prev,
        irs: {
          ...prev.irs,
          total_sprayed_house: totalDusunSprayed,
        },
      }));
    }
    setRowErrors({});
  }, [isDusun, totalDusunSprayed, totalTargeted]);

  useEffect(() => {
    if (totalTargeted <= 0) {
      setSummaryError("");
      return;
    }

    if (isDusun && totalDusunTargeted > totalTargeted) {
      setSummaryError(
        `Total sasaran rumah dusun (${totalDusunTargeted.toLocaleString()}) melebihi total sasaran rumah desa (${totalTargeted.toLocaleString()}).`
      );
      return;
    } else if (totalSprayed > totalTargeted) {
      setSummaryError(
        `Jumlah total rumah yang disemprot (${totalSprayed.toLocaleString()}) melebihi jumlah total sasaran rumah (${totalTargeted.toLocaleString()}).`
      );
      return;
    }
    setSummaryError("");
  }, [
    isDusun,
    totalSprayed,
    totalDusunSprayed,
    totalTargeted,
    totalDusunTargeted,
  ]);

  const clampTotalSprayedAndForward = (e) => {
    const raw = toInt(e.target.value);

    if (totalTargeted <= 0) {
      return handleInputChange(e, "irs");
    }

    if (!isDusun) {
      const capped = Math.min(raw, totalTargeted);
      if (capped !== raw) {
        setFormData((prev) => ({
          ...prev,
          irs: { ...prev.irs, total_sprayed_house: capped },
        }));
        setFieldErrorWithAutoClear(
          "total_sprayed_house",
          `Nilai dibatasi ke ${capped.toLocaleString()} karena tidak boleh melebihi jumlah total sasaran rumah (${totalTargeted.toLocaleString()}).`
        );
        return;
      }
    }

    handleInputChange(e, "irs");
  };

  useEffect(() => {
    if (!isDusun && totalSprayed > totalTargeted && totalTargeted > 0) {
      const capped = Math.min(totalSprayed, totalTargeted);
      setFormData((prev) => ({
        ...prev,
        irs: { ...prev.irs, total_sprayed_house: capped },
      }));
      setFieldErrorWithAutoClear(
        "total_sprayed_house",
        `Nilai dibatasi ke ${capped.toLocaleString()} karena tidak boleh melebihi jumlah total sasaran rumah (${totalTargeted.toLocaleString()}).`
      );
    }
    setRowErrors({});
  }, [totalTargeted, isDusun]);

  const handleChangeTargetedHouses = (e, index) => {
    const incoming = toInt(e.target.value);
    const list = [...(irs.sub_villages_data || [])];
    const currentRow = list[index] || {};

    const othersTargetSum = list.reduce(
      (sum, v, idx) =>
        idx === index ? sum : sum + toInt(v && v.targeted_houses),
      0
    );

    const capByTargetedTotal = Math.max(totalTargeted - othersTargetSum, 0);

    const clampedTargeted = Math.min(incoming, capByTargetedTotal);

    let violatedMsg = "";
    if (incoming > clampedTargeted) {
      violatedMsg = `Tidak boleh melebihi sisa kuota total sasaran rumah desa (${capByTargetedTotal.toLocaleString()}).`;
    }

    setRowErrors((prev) => ({
      ...prev,
      [`targeted_houses_${index}`]: violatedMsg,
    }));

    list[index] = {
      ...list[index],
      targeted_houses: clampedTargeted,
    };

    const currentSprayed = toInt(currentRow.sprayed_houses);
    let newTotalDusunSprayed = totalDusunSprayed;

    if (currentSprayed > clampedTargeted) {
      const cappedSprayed = clampedTargeted;
      list[index].sprayed_houses = cappedSprayed;
      const othersSprayedSum = list.reduce(
        (sum, v, idx) =>
          idx === index ? sum : sum + toInt(v && v.sprayed_houses),
        0
      );
      newTotalDusunSprayed = othersSprayedSum + cappedSprayed;

      setRowErrors((prev) => ({
        ...prev,
        [`sprayed_houses_${index}`]: `Nilai disemprot dibatasi ke ${cappedSprayed.toLocaleString()} karena melebihi sasaran rumah dusun yang baru.`,
      }));
    } else {
      setRowErrors((prev) => ({ ...prev, [`sprayed_houses_${index}`]: "" }));
    }

    setFormData((prev) => ({
      ...prev,
      irs: {
        ...prev.irs,
        sub_villages_data: list,
        ...(isDusun && currentSprayed > clampedTargeted
          ? { total_sprayed_house: newTotalDusunSprayed }
          : {}),
      },
    }));
  };

  const handleChangeSprayedHouses = (e, index) => {
    const incoming = toInt(e.target.value);
    const list = [...(irs.sub_villages_data || [])];
    const currentRow = list[index] || {};

    const othersSum = list.reduce(
      (sum, v, idx) =>
        idx === index ? sum : sum + toInt(v && v.sprayed_houses),
      0
    );

    const capByTargetedTotal = Math.max(totalTargeted - othersSum, 0);

    const rowTargeted = toInt(currentRow.targeted_houses);
    const capByRowTarget = rowTargeted > 0 ? rowTargeted : Infinity;

    const hardMax = Math.min(capByTargetedTotal, capByRowTarget);
    const clamped = Math.min(incoming, hardMax);

    let violatedMsg = "";
    if (incoming > clamped) {
      if (capByRowTarget < capByTargetedTotal) {
        violatedMsg = `Tidak boleh melebihi total sasaran rumah pada dusun ini (${rowTargeted.toLocaleString()}).`;
      } else {
        violatedMsg = `Tidak boleh melebihi sisa kuota total sasaran rumah desa (${capByTargetedTotal.toLocaleString()}).`;
      }
    }

    setRowErrors((prev) => ({
      ...prev,
      [`sprayed_houses_${index}`]: violatedMsg,
      [`targeted_houses_${index}`]: "",
    }));

    list[index] = {
      ...list[index],
      sprayed_houses: clamped,
    };

    const newTotalDusun = othersSum + clamped;

    setFormData((prev) => ({
      ...prev,
      irs: {
        ...prev.irs,
        sub_villages_data: list,
        ...(isDusun ? { total_sprayed_house: newTotalDusun } : {}),
      },
    }));
  };

  return (
    <div className="irs-fields">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 20,
          marginTop: 20,
        }}
      >
        <InputDateTime
          label="Tanggal IRS"
          name="irs_date"
          dateFormat="YYYY-MM-DD"
          placeholder="YYYY-MM-DD"
          value={irs.irs_date}
          onChange={(e) =>
            handleInputChange(
              {
                target: {
                  name: "irs_date",
                  value: moment(e).format("YYYY-MM-DD"),
                },
              },
              "irs"
            )
          }
          isRequired={true}
        />
        <InputSelect
          label="Jenis insektisida"
          name="insecticide_type"
          placeholder="--PILIH--"
          data={INSECTICIDE_TYPE_OPTIONS}
          onChange={(selected) => {
            setFormData((prev) => ({
              ...prev,
              irs: {
                ...prev.irs,
                insecticide_type: selected?.value || "",
              },
            }));
          }}
          value={memoizedValues.irs.insecticideType}
          isRequired={true}
        />
        <InputField
          label="Jumlah insektisida yang digunakan"
          type="number"
          placeholder="0"
          name="total_insecticide"
          onChange={(e) => handleInputChange(e, "irs")}
          value={irs.total_insecticide}
          isRequired={true}
        />
        <InputSelect
          label="Satuan insektisida"
          name="total_insecticide_unit"
          placeholder="--PILIH--"
          data={INSECTICIDE_UNIT_OPTIONS}
          onChange={(selected) => {
            setFormData((prev) => ({
              ...prev,
              irs: {
                ...prev.irs,
                total_insecticide_unit: selected?.value || "",
              },
            }));
          }}
          value={memoizedValues.irs.insecticideUnit}
          isRequired={true}
        />
        <InputField
          label="Jumlah total sasaran rumah"
          type="number"
          placeholder="0"
          name="total_targeted_house"
          onChange={(e) => {
            setRowErrors({});
            handleInputChange(e, "irs");
          }}
          value={irs.total_targeted_house}
          isRequired={true}
        />
        <div>
          <InputField
            label="Jumlah total rumah yang disemprot"
            type="number"
            placeholder={isDusun ? "Otomatis dari total dusun" : "0"}
            name="total_sprayed_house"
            onChange={clampTotalSprayedAndForward}
            value={irs.total_sprayed_house}
            isRequired={true}
            disabled={isDusun}
          />

          {!isDusun && fieldErrors.total_sprayed_house ? (
            <div style={{ color: "#d9534f", fontSize: 12, marginTop: 6 }}>
              {fieldErrors.total_sprayed_house} 
            </div>
          ) : null}
        </div>
        <InputSelect
          label="Intervensi sampai tingkat dusun?"
          name="is_dusun_level_intervention"
          placeholder="--PILIH--"
          data={YesAndNoOptions}
          onChange={(selected) => {
            const yes = selected?.value === "Ya";
            setFormData((prev) => ({
              ...prev,
              irs: {
                ...prev.irs,
                is_dusun_level_intervention: selected?.value || "",
                ...(yes
                  ? {}
                  : {
                      total_sub_villages: 0,
                      sub_villages_data: [],
                    }),
              },
            }));
            setRowErrors({});
          }}
          value={memoizedValues.irs.isDusunLevel}
        />
        {irs.is_dusun_level_intervention === "Ya" && (
          <InputField
            label="Jumlah dusun"
            type="number"
            placeholder="0"
            name="total_sub_villages"
            onChange={(e) => {
              setRowErrors({});
              handleInputChange(e, "irs");
            }}
            value={irs.total_sub_villages}
            isRequired={true}
          />
        )}
      </div>
      {toInt(irs.total_sub_villages) > 0 && (
        <div style={{ marginTop: 20 }}>
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th
                    style={{
                      minWidth: 200,
                      backgroundColor: "#f8f9fa",
                      textAlign: "center",
                    }}
                  >
                    Nama dusun
                  </th>
                  <th
                    style={{
                      minWidth: 200,
                      backgroundColor: "#f8f9fa",
                      textAlign: "center",
                    }}
                  >
                    Jumlah penduduk dusun
                  </th>
                  <th
                    style={{
                      minWidth: 250,
                      backgroundColor: "#f8f9fa",
                      textAlign: "center",
                    }}
                  >
                    Jumlah total sasaran rumah (tingkat dusun
                  </th>
                  <th
                    style={{
                      minWidth: 250,
                      backgroundColor: "#f8f9fa",
                      textAlign: "center",
                    }}
                  >
                    Jumlah total rumah yang disemprot (tingkat dusun)
                  </th>
                </tr>
              </thead>
              <tbody>
                {Array.from({
                  length: parseInt(irs.total_sub_villages) || 0,
                }).map((_, index) => {
                  const subVillageData = (irs.sub_villages_data &&
                    irs.sub_villages_data[index]) || {
                    village_name: "",
                    population: "",
                    targeted_houses: "",
                    sprayed_houses: "",
                  };

                  return (
                    <tr key={index}>
                      <td style={{ padding: "10px" }}>
                        <InputField
                          placeholder={`Nama dusun ${index + 1}`}
                          name="village_name"
                          value={subVillageData.village_name}
                          onChange={(e) => {
                            const updated = [...(irs.sub_villages_data || [])];
                            updated[index] = {
                              ...updated[index],
                              village_name: e.target.value,
                            };
                            setFormData((prev) => ({
                              ...prev,
                              irs: { ...prev.irs, sub_villages_data: updated },
                            }));
                          }}
                        />
                      </td>
                      <td style={{ padding: "10px" }}>
                        <InputField
                          type="number"
                          placeholder="0"
                          name="population"
                          value={subVillageData.population}
                          onChange={(e) => {
                            const updated = [...(irs.sub_villages_data || [])];
                            updated[index] = {
                              ...updated[index],
                              population: e.target.value,
                            };
                            setFormData((prev) => ({
                              ...prev,
                              irs: { ...prev.irs, sub_villages_data: updated },
                            }));
                          }}
                        />
                      </td>
                      <td style={{ padding: "10px" }}>
                        <InputField
                          type="number"
                          placeholder="0"
                          name="targeted_houses"
                          value={subVillageData.targeted_houses}
                          onChange={(e) => handleChangeTargetedHouses(e, index)}
                        />
                        {rowErrors[`targeted_houses_${index}`] ? (
                          <div
                            style={{
                              color: "#d9534f",
                              fontSize: 12,
                              marginTop: 6,
                            }}
                          >
                            {rowErrors[`targeted_houses_${index}`]} 
                          </div>
                        ) : null}
                      </td>
                      <td style={{ padding: "10px" }}>
                        <InputField
                          type="number"
                          placeholder="0"
                          name="sprayed_houses"
                          value={subVillageData.sprayed_houses}
                          onChange={(e) => handleChangeSprayedHouses(e, index)}
                        />
                        {rowErrors[`sprayed_houses_${index}`] ? (
                          <div
                            style={{
                              color: "#d9534f",
                              fontSize: 12,
                              marginTop: 6,
                            }}
                          >
                            {rowErrors[`sprayed_houses_${index}`]}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-3 p-3 bg-light rounded">
            <div className="row">
              <div className="col-md-3">
                <small className="text-muted">
                  Total penduduk semua dusun:
                </small>
                <div className="font-weight-bold">
                  {(irs.sub_villages_data || [])
                    .reduce((s, v) => s + toInt(v && v.population), 0)
                    .toLocaleString()}{" "}
                  orang
                </div>
              </div>
              <div className="col-md-3">
                <small className="text-muted">
                  Total sasaran rumah (dusun):
                </small>
                <div className="font-weight-bold">
                  {totalDusunTargeted.toLocaleString()} rumah
                </div>
              </div>
              <div className="col-md-3">
                <small className="text-muted">
                  Total rumah disemprot (dusun):
                </small>
                <div className="font-weight-bold">
                  {totalDusunSprayed.toLocaleString()} rumah
                </div>
              </div>
              <div className="col-md-3">
                <small className="text-muted">
                  Sisa kuota sasaran rumah desa:
                </small>
                <div
                  className={`font-weight-bold ${
                    totalDusunSprayed > totalTargeted ||
                    totalDusunTargeted > totalTargeted
                      ? "text-danger"
                      : ""
                  }`}
                >
                  {Math.max(
                    totalTargeted - totalDusunTargeted,
                    0
                  ).toLocaleString()}
                  rumah
                </div>
              </div>
            </div>
            {summaryError && (
              <div className="mt-2" style={{ color: "#d9534f", fontSize: 13 }}>
                {summaryError}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IRSFields;
